import React, { useEffect, useState } from "react";
import {
  ComponentSingleStyleConfig,
  useDisclosure,
  useOutsideClick,
  useStyleConfig,
} from "@chakra-ui/react";
import { ComponentStyleProps } from "../../../../lib/styles/props/component-style-props";
import { CMButtonSearchCondition } from "../CMButtonSearchCondition/CMButtonSearchCondition";
import { CMButtonPopupSearchConditionMaster } from "../../button-popup";
import { isNullish } from "../../../../lib/util/common-util";
import {
  SearchDropdownDisplayValue,
  SearchDropdownOption,
} from "../../../../lib";
import log from "loglevel";
import { useDebounce } from "react-use";

export type CMButtonSearchConditionMasterProps<T extends string | number> = {
  label: string;
  placeholder: string;
  /**
   * 入力可能な最大文字数。
   */
  maxLength?: number;

  /**
   * メニューの最大表示数
   */
  menuShowMaxCount?: number;

  value?: T | null;

  hideClearButton?: boolean;

  labelSubmit?: string;
  labelClear?: string;
  searchResultNone?: string;

  /**
   * 値を表示値に変換する関数。
   * heading: 各行の手前に、薄い字で表示する部分。必須ではない。
   * main: 表示値。必須。
   */
  toDisplayValue: (value: T) => Promise<SearchDropdownDisplayValue>;

  /**
   * 検索を行う関数。
   * @param text 検索条件となるテキスト。
   * @return 検索結果。
   */
  onSearch: (text: string) => Promise<T[]>;
  onChange?: (value: T | null) => void;

  /**
   * ポップオーバーを出す時、portalを用いるならtrue。
   * デフォルト値はfalse。
   */
  usePortal?: boolean;
} & ComponentStyleProps;

export const CMButtonSearchConditionMaster = <T extends string | number>({
  label,
  placeholder,
  maxLength = 100,

  menuShowMaxCount,

  value = null,

  hideClearButton = false,

  labelSubmit,
  labelClear,
  searchResultNone,

  toDisplayValue,

  onSearch,
  onChange,

  usePortal = false,

  sx,
  ...rest
}: CMButtonSearchConditionMasterProps<T>) => {
  const styles = useStyleConfig("CMButtonSearchConditionMasterStyles");

  const [buttonDisplayValue, setButtonDisplayValue] = useState<string | null>(
    null,
  );
  // 選択値をボタン上の表示値に変換する。
  useEffect(
    () => {
      if (isNullish(value)) {
        setButtonDisplayValue(null);
      } else {
        (async () => {
          setButtonDisplayValue((await toDisplayValue(value)).mainLabel);
        })();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value],
  );

  // ポップアップエリアの表示制御に使用する
  const {
    isOpen: isOpenPopup,
    // onOpen: onOpenPopup,
    onClose: onClosePopup,
    onToggle: onTogglePopup,
  } = useDisclosure();

  const {
    isOpen: isOpenMenu,
    onOpen: onOpenMenu,
    onClose: onCloseMenu,
  } = useDisclosure();

  // 入力文字列
  const [inputText, setInputText] = useState<string>("");
  // const debouncedInputText = useDebounce<string>(inputText, 1000);

  // 選択肢一覧の値部分のみ。
  const [optionValues, setOptionValues] = useState<T[]>([]);

  // 選択肢一覧。
  const [options, setOptions] = useState<SearchDropdownOption<T>[]>([]);

  // 確定ボタンが押下されるまで、内部で選択項目を保持する
  const [internalSelectedValue, setInternalSelectedValue] = useState<T | null>(
    value,
  );

  const resetAllInternalState = () => {
    setInputText("");
    setOptionValues([]);
    setOptions([]);
    setInternalSelectedValue(null);
    onCloseMenu();
  };

  const handleSelectOption = (value: T) => {
    setInternalSelectedValue(value);

    setInputText("");
    setOptionValues([]);
    setOptions([]);

    onCloseMenu();
  };

  const handleChangeInputText = (value: string) => {
    setInputText(value);
  };
  const handleClearInternal = () => {
    resetAllInternalState();
  };

  useDebounce(
    () => {
      log.debug(`debounced onSearch: inputText=${inputText}`);
      if (isNullish(internalSelectedValue) && inputText !== "") {
        (async () => {
          const _optionValues = await onSearch(inputText);
          setOptionValues(_optionValues);
          log.debug(
            `debounced onSearch: setOptionValues: optionValues=${JSON.stringify(
              _optionValues,
            )}`,
          );
        })();
      }
    },
    1000,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputText],
  );

  useEffect(
    () => {
      log.debug(
        `CMButtonPopupSearchConditionMaster: useEffect on optionValues=${JSON.stringify(
          optionValues,
        )}`,
      );
      (async () => {
        const _options = await Promise.all(
          optionValues.map(async (v) => ({
            value: v,
            ...(await toDisplayValue(v)),
          })),
        );
        setOptions(_options);
        // if (isNullish(internalSelectedValue) && _options.length > 0) {
        if (inputText.length > 0 && isNullish(internalSelectedValue)) {
          onOpenMenu();
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [optionValues],
  );

  /**
   * この検索条件全体を確定する。
   */
  const handleSubmit = () => {
    onClosePopup();
    resetAllInternalState();
    onChange?.(internalSelectedValue);
  };

  /**
   * この検索条件全体をクリアする。
   */
  const handleClear = () => {
    onClosePopup();
    resetAllInternalState();
    onChange?.(null);
  };

  const ref = React.useRef(null);
  useOutsideClick({
    ref: ref,
    handler: () => {
      onClosePopup();
      resetAllInternalState();
    },
  });

  return (
    <CMButtonSearchCondition
      label={label}
      displayValue={buttonDisplayValue}
      onClick={onTogglePopup}
      isOpenPopup={isOpenPopup}
      sx={{ ...styles, ...sx }}
      usePortal={usePortal}
      ref={ref}
      {...rest}
    >
      <CMButtonPopupSearchConditionMaster<T>
        placeholder={placeholder}
        maxLength={maxLength}
        menuShowMaxCount={menuShowMaxCount}
        labelSubmit={labelSubmit}
        labelClear={labelClear}
        hideClearButton={hideClearButton}
        searchResultNone={searchResultNone}
        toDisplayValue={toDisplayValue}
        inputText={inputText}
        onChangeInputText={handleChangeInputText}
        onClearInputText={handleClearInternal}
        options={options}
        onSelectOption={handleSelectOption}
        internalSelectedValue={internalSelectedValue}
        onSubmit={handleSubmit}
        onClear={handleClear}
        searchResultMenuSettings={{ isOpenMenu, onCloseMenu }}
      />
    </CMButtonSearchCondition>
  );
};

export const CMButtonSearchConditionMasterStyles: ComponentSingleStyleConfig = {
  baseStyle: {},
};
